// Site navigation for the configurator header — so /build isn't a dead-end.
// Desktop (>=lg): inline items with hover dropdowns (mouse). Tablet + mobile
// (<lg): a hamburger drawer with tap-to-expand accordions (touch). Links point
// to the main gammill.com site and open same-tab; the build is auto-saved to
// localStorage (see store) so leaving and coming back restores it.

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface MenuItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const SITE = "https://gammill.com";

const MENU: MenuItem[] = [
  {
    label: "Buyer's Guide",
    href: `${SITE}/machines/buyers-guide-2/`,
    children: [
      { label: "Compare Models", href: `${SITE}/machines/buyers-guide-2/compare-machines/` },
      { label: "Machine Size", href: `${SITE}/machines/buyers-guide-2/selecting-machine-size/` },
      { label: "Selecting a Table", href: `${SITE}/machines/buyers-guide-2/selecting-a-table/` },
      { label: "Pre-Loved Gammill", href: `${SITE}/preloved/` },
    ],
  },
  { label: "Financing", href: `${SITE}/machines/buyers-guide-2/financing/` },
  {
    label: "Longarm Business",
    href: `${SITE}/owners/education/longarm-business/`,
    children: [
      { label: "Quilting Business Success", href: `${SITE}/quilting-business-success/` },
      { label: "Download Business Plan", href: `${SITE}/owners/education/sample-business-plan/` },
    ],
  },
  {
    label: "Why Choose Gammill?",
    href: `${SITE}/why-choose-gammill/`,
    children: [
      { label: "Setup", href: `${SITE}/why-choose-gammill-setup/` },
      { label: "Training", href: `${SITE}/why-choose-gammill-training/` },
      { label: "Video Series", href: `${SITE}/owners/online-show-series/` },
      { label: "Warranty", href: `${SITE}/warranty/` },
      { label: "Contact Us", href: `${SITE}/about/contact-us/` },
    ],
  },
  { label: "Home", href: `${SITE}/` },
];

function Chevron({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={"h-3 w-3 " + className} fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

export function SiteNav() {
  return (
    <>
      <DesktopNav />
      <MobileNav />
    </>
  );
}

/* ── Desktop: inline, hover dropdowns ─────────────────────────────── */
function DesktopNav() {
  return (
    <nav className="hidden items-center gap-1 lg:flex">
      {MENU.map((item) => (
        <div key={item.label} className="group relative">
          <a
            href={item.href}
            className="flex items-center gap-1 whitespace-nowrap px-3 py-2 font-sans text-[14px] font-semibold text-white/85 transition hover:text-white"
          >
            {item.label}
            {item.children && <Chevron className="text-white/50" />}
          </a>
          {item.children && (
            <div className="invisible absolute right-0 top-full z-50 min-w-[220px] translate-y-1 border border-line bg-white opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              {item.children.map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  className="block px-4 py-2.5 font-sans text-[14px] text-ink transition hover:bg-mist"
                >
                  {c.label}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}

/* ── Tablet + mobile: hamburger drawer with accordions ────────────── */
function MobileNav() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="flex h-9 w-9 items-center justify-center text-white"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" aria-hidden>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col bg-white shadow-2xl"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25 }}
            >
              <div className="flex items-center justify-between border-b border-line bg-ink px-4 py-4">
                <span className="font-display text-base font-extrabold text-white">Menu</span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="flex h-8 w-8 items-center justify-center text-white/80 hover:text-white"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" aria-hidden>
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto">
                {MENU.map((item) =>
                  item.children ? (
                    <div key={item.label} className="border-b border-line">
                      <button
                        type="button"
                        onClick={() =>
                          setExpanded((e) => (e === item.label ? null : item.label))
                        }
                        className="flex w-full items-center justify-between px-4 py-3.5 text-left font-sans text-[15px] font-semibold text-ink"
                      >
                        {item.label}
                        <Chevron
                          className={
                            "text-steel transition-transform " +
                            (expanded === item.label ? "rotate-180" : "")
                          }
                        />
                      </button>
                      {expanded === item.label && (
                        <div className="bg-mist/40 pb-1">
                          <a
                            href={item.href}
                            className="block px-6 py-2.5 font-sans text-[14px] font-medium text-ink"
                          >
                            {item.label} — overview
                          </a>
                          {item.children.map((c) => (
                            <a
                              key={c.label}
                              href={c.href}
                              className="block px-6 py-2.5 font-sans text-[14px] text-graphite"
                            >
                              {c.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      className="block border-b border-line px-4 py-3.5 font-sans text-[15px] font-semibold text-ink"
                    >
                      {item.label}
                    </a>
                  ),
                )}
              </nav>

              <p className="border-t border-line px-4 py-3 font-serif text-[13px] italic text-steel">
                Your build is saved — you can leave and come back to it.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
